import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'Vendor/Store/store';
import { saveCreation } from 'Vendor/Store/studio/thunk';
import { clearHistory } from 'Vendor/Store/undoable';

interface Props {
    creationId?: number;
    debounceMs?: number;
    enabled?: boolean;
}

const useAutoSaveCreation = ({
    creationId,
    debounceMs = 2500,
    enabled = true,
}: Props) => {
    const dispatch = useDispatch<AppDispatch>();

    const pastLength = useSelector((s: RootState) => s.studio.past.length);
    const pastLengthRef = useRef(pastLength);

    useEffect(() => {
        pastLengthRef.current = pastLength;
    }, [pastLength]);

    const timerRef = useRef<number | null>(null);
    const savePromiseRef = useRef<Promise<void> | null>(null);
    const resaveRef = useRef(false);

    const [hasPendingSave, setHasPendingSave] = useState(false);

    const clearTimer = useCallback(() => {
        if (timerRef.current !== null) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    }, []);

    const runSave = useCallback(async () => {
        if (!creationId || pastLengthRef.current === 0) {
            setHasPendingSave(false);
            return;
        }

        if (savePromiseRef.current) {
            resaveRef.current = true;
            return savePromiseRef.current;
        }

        const task = (async () => {
            do {
                resaveRef.current = false;
                const pastBeforeSave = pastLengthRef.current;
                await dispatch(saveCreation()).unwrap();
                if (pastLengthRef.current === pastBeforeSave) {
                    dispatch(clearHistory());
                }
            } while (resaveRef.current);
        })();

        savePromiseRef.current = task;

        try {
            await task;
        } finally {
            savePromiseRef.current = null;
            setHasPendingSave(pastLengthRef.current > 0);
        }
    }, [dispatch, creationId]);

    const scheduleSave = useCallback(() => {
        if (!enabled || pastLength <= 0) {
            setHasPendingSave(false);
            return;
        }

        clearTimer();

        timerRef.current = window.setTimeout(() => {
            runSave().catch(() => {});
            timerRef.current = null;
        }, debounceMs);

        setHasPendingSave(true);
    }, [enabled, debounceMs, pastLength, runSave, clearTimer]);

    useEffect(() => {
        scheduleSave();
        return clearTimer;
    }, [pastLength, creationId, scheduleSave, clearTimer]);

    useEffect(() => {
        const flush = () => {
            clearTimer();
            runSave().catch(() => {});
        };

        window.addEventListener('pagehide', flush);
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'hidden') flush();
        });

        return () => {
            window.removeEventListener('pagehide', flush);
        };
    }, [runSave, clearTimer]);

    const forceSave = useCallback(async () => {
        clearTimer();
        await runSave();
    }, [runSave, clearTimer]);

    return { forceSave, hasPendingSave };
}

export default useAutoSaveCreation;
